/**
 * cabinetLayout schema constants + JSDoc typedefs.
 *
 * `room.cabinetLayout` captures the typology, cabinet-wall set, virtual
 * walls (for peninsulas/islands), and appliance anchors for any room
 * with cabinetry. Naming is generic — kitchens are V1 but the schema
 * also applies to wet bars in family rooms, butler's pantries,
 * laundry rooms, etc.
 *
 * Reference: claude-docs/2026-05-12-cabinet-layout-integration.md
 */

/** All valid `cabinetWall.kind` values. */
const CABINET_WALL_KINDS = Object.freeze({
  REAL: 'real',
  PENINSULA: 'peninsula',
  ISLAND: 'island',
});

/** All valid `cabinetWall.anchor.end` values for peninsulas. */
const ANCHOR_ENDS = Object.freeze({
  LEFT: 'left',
  RIGHT: 'right',
});

/** All valid `layoutProvenance.sourceKey` values on derived objects. */
const PROVENANCE_SOURCE_KEYS = Object.freeze({
  AUTO: 'auto', // emitted by Phase B; safe to overwrite on regen
  USER_EDITED: 'userEdited', // user has moved/customized; preserve on regen
});

/**
 * @typedef {Object} KitchenArea
 * @property {number} version              Schema version of this override (1).
 * @property {Array<{x:number,z:number}>} polygon  Resolved kitchen region —
 *   the source of truth the layout pipeline consumes. CCW, X-Z plane.
 * @property {string[]} contributingRoomIds Rooms whose walls/floors feed the
 *   kitchen workspace (one room until the cross-room phase).
 * @property {Object} [provenance]         How the polygon was built, for
 *   re-derivation / staleness detection (populated by the editor in Phase 1b).
 */

/** Current cabinetLayout generator version. Bump for breaking shape changes
 * OR generator-output changes — it's part of the layout cache key
 * (cabinetLayoutService), so a bump invalidates stale cached layouts that
 * predate the change. (1.61.0: two fixes around a peninsula's dining side. A
 * seated PENINSULA is now surfaced: its seating is decided after scoring, so
 * `scoring/barstoolSeats` cannot credit it and a group's best-scoring member
 * was routinely its unseated twin — the card offered was the unseated one in
 * four of nine groups on 50 Park St. `seatedRepresentative` asks the real
 * materializer, bounded to the top groups and to members within the seating
 * credit, and promotes the seated one (ordering only; no score is rewritten).
 * Separately `subtractBlockers` filtered leftover spans with a bare
 * `>= minWidth`: a bay measuring EXACTLY the 6" minimum fell either side of it
 * on the last bit of a double, so the same 6" bay was a cabinet at one end of
 * an arm and bare floor at the other, leaving a peninsula standing 6" off its
 * own wall run. The filter now carries the `1e-6` that function already used.
 * 1.60.0: a window a fridge would occlude now ends a
 * cabinet run, so `endpointSnap.seatFridgeAtOpenRunEnd` can no longer reseat
 * the fridge into a span the enumerator rejected. It was handed only
 * doors + openings, so a 72"-wide, 33"-sill window read as bare wall and the
 * "open end" landed inside it — 50 Park St put the fridge, its 84" surround
 * and an over-fridge upper across that window in every top-ranked layout. The
 * guard lives inside the pass (both call sites hand it the raw windows +
 * floorY), and `scoring/fridgeRunEnd` now sources the same blocker set from
 * `tallApplianceBlockingOpenings` so the +5 run-end credit and the seat agree
 * on where a run ends. A high transom (sill >= 7') still doesn't block. Moves
 * fridges and reorders layouts, so cached layouts are stale.
 * Same release: a peninsula / floating-U arm can finally anchor somewhere
 * other than flush at a wall extremity. Every call site passed
 * `cornerOffset: 0` and `buildFloatingUPeninsulas` hard-coded it, so anything
 * parked at that extremity — a door swing, a low-sill window's front
 * clearance — killed the whole typology rather than the arm sliding over. At
 * 50 Park St that cost the ENTIRE other-side peninsula: 23" of a 72" window
 * sat on the wall's last inches, so every U pointed the same way and no leg
 * appeared on that side. `peninsulaAnchorOffsets` now derives the minimum
 * inboard slide that clears the blocking zones (by `OPENING_BUFFER`, the same
 * margin `getAvailableWallSegments` keeps) and the enumerator tries flush
 * first, then that. All four floating-U builds route through one
 * `buildFloatingUAtBestAnchor` — the arm-extended and L-peninsula variants
 * used to re-derive a flush arm and let the fixed case back in. The peninsula
 * SEATING OVERHANG's reach to a wall end is now gated on opening zones as
 * well as walkways (it was laying 1.5" of slab across a 32.5" sill), and
 * `fitSWPeninsula` tries both wall ends instead of silently defaulting to
 * `'left'` — feasibility must not turn on a wall's stored winding. Adds
 * layouts on kitchens with a blocked wall end, so cached layouts are stale.
 * Same release, the arm-to-wall CORNER: an arm's first bay is often a filler
 * narrower than a cabinet, and the sub-12" rule requires such a bay to abut
 * something real at both ends. `abutsRealAtLF` read the wall's length from
 * `wall.dimensions.x`, a field a VIRTUAL peninsula wall does not carry, so the
 * length was 0 — making "at the wall's own end" vacuously true at the `end`
 * edge and true at `start` only exactly at param 0. Which end that favoured
 * was the arm's WINDING, which `virtualPeninsulaWall` picks by distance from
 * the kitchen centroid, so the same layout kept its 6" corner filler wound one
 * way and dropped it — leaving bare floor between the wall cabinets' front
 * face and the arm — wound the other. Length now comes from the wall AXIS
 * (also stale on a `getKitchenFinalWalls`-clipped wall, which rewrites
 * `alignment` only), and a corner RESERVATION counts as a real abutment since
 * it is held for the perpendicular run's body. The seating overhang is
 * gated on those zones too: it is 12" wider than the arm, so it nipped a
 * window's clearance the run beneath it cleared, and trimming it back stepped
 * the counter edge — a face whose strip cannot run the whole seated span is
 * now left UNSEATED (no overhang, no stools) instead. That gate reads
 * `getPeninsulaDoorZones`, not the padded `clearanceZones` — a peninsula may
 * sit in a door's 54" approach buffer and is bound only by the true swing —
 * and carries the corridor graze tolerance.
 * 1.59.0: the corner where a peninsula arm dies into a
 * REAL wall. That wall is clipped to the kitchen SECTION — the peninsula's
 * outer face — so it ran the arm's 1" back-panel strip full of its own
 * cabinetry: the arm's panel then stopped at the corner box and the crossing
 * cabinet's bare end finished the peninsula, showing its 3"x4" toe-kick void
 * where the panel took over, while the slab that caps the panel added its inch
 * on top of a run already covering it and hung an inch past the finished face.
 * The strip is now reserved on that wall (lower-only; single-sided arms only —
 * a double-row leg runs its outer row to the outer corner), so the crossing run
 * stops ON the arm's line, the panel spans the corner box, and the slab's
 * growth is the RESIDUAL to the peninsula's outer face rather than a flat
 * panel thickness. 1.58.0: three fixes at the corner where two
 * peninsula arms meet. The corner slab is sized on wall A only, so it
 * reached just to wall B's wall LINE and left B's 1" back panel bare for
 * the corner box's width, stepping 1" against B's own counter; it now
 * runs one panel thickness past the corner. Joining an arm to a real wall
 * snapped the arm's END onto that wall's endpoint, pivoting the arm about
 * its far end — 1" of offset tilts a 9 ft arm half a degree — so the snap
 * is gone and `findSharedCorners` accepts an arm's end within the
 * back-panel inset instead, taking the wall lines' intersection as the
 * corner. And `findSharedCorners` stopped at the first pair of walls at a
 * vertex, where three meet whenever a run is split there; it now reports
 * every pair, so a corner can no longer be handed to a non-cabinet wall
 * and dropped, leaving two runs to fill the same corner box. Also, the back
 * panel now follows the spans the arm actually CARRIES instead of the whole
 * wall — 1.52.0 removed it from an arm carrying nothing, but an arm can carry
 * cabinetry at both ends and nothing in between, leaving a bare 1" strip in
 * the gap. Changes
 * And a walkway that clips an arm's END now TRIMS the arm: the gate already
 * measured that stretch and accepted the trim, but discarded it, so the
 * generator scored a 95" peninsula and built 91.6" on it. Changes
 * peninsula geometry AND ranking, so cached layouts are stale.
 * 1.56.0: the end piece closing a run against an
 * appliance is sized as panel stock PLUS the blocking it fastens to — 3/4"
 * panel on a 2x4, so 4.25" flat or 2.25" on edge — instead of a 3/4" panel
 * floating on nothing. A reservation gets the 2.25" minimum. 1.55.0: a run may not terminate on a dishwasher — the
 * exposed side now gets a finished end panel, emitted as a normal blank-panel
 * so the run grouper and the countertop pass carry it, and sized to the free
 * span beside the appliance (nothing when a real cabinet fits there, the whole
 * strip when it is narrower, 3/4" taken from a reservation when none is free).
 * 1.54.0: a range whose vent hood would land in an
 * opening-clearance zone is slid along its wall until the hood clears (before
 * scoring, like the fridge reseat), and the candidate is dropped when no
 * position on its free span works — the materializer used to drop the hood and
 * keep the range, shipping a range with no hood in 6% of range-bearing
 * layouts. 1.53.0: a clearance zone that obstructs nothing at
 * counter height no longer reserves space away from the LOWER fill — a base
 * run tucks under a high-sill window on a perpendicular wall instead of
 * stopping 30" short of the corner while the uppers above it reach it
 * (#1575). Doors and door-less openings run to the floor and still block.
 * 1.48.0: island seating is gated on CIRCULATION
 * surviving, not on the drawn walkway rectangle being untouched. A
 * corridor is one instantiation of a path, so an edge whose seating strip
 * lands in one needs 59" of free depth (the 23" strip + a full 36"
 * walkway behind the stools) while an edge clear of every corridor needs
 * the 48" seated-diner depth. The old overlap veto rejected islands
 * carrying 61" and 73" of aisle. Islands seat where they could not before
 * (819 Montrose: 0 → 86 seated island layouts), and a new `barstoolSeats`
 * soft rule awards +5 to a layout whose island seats, so island ranking
 * shifts too. The preview re-routes drawn corridors clear of the stools
 * (presentational only). Separately, an ALIGNED peninsula arm no longer
 * waives the walkway gate: it was admitted on the unchecked premise that its
 * tip squares off the U mouth rather than pinching it, which let a 176" arm
 * sit with 96" of itself inside a walkway. Alignment does not justify
 * blocking circulation, so both waiver sites (L+peninsula aligned arm,
 * floating-U extended far arm) now gate like any other peninsula — removing
 * arms that block 74"-208" of walkway, and with them the peninsula option
 * for two fixture kitchens whose only peninsulas did that. New objects,
 * FEWER layouts AND changed ranking, so cached layouts are stale.
 * 1.47.0: islands and peninsulas now SEAT BARSTOOLS
 * where there is room for them. A seated edge's countertop cantilevers 12"
 * (NKBA knee overhang) instead of the 1" reveal and carries one stool per
 * 24", emitted automatically — no user toggle. The gate is 48" of free
 * depth off the cabinet face (12" overhang + a 36" seated diner) with no
 * seating STRIP (the 12" cantilever + the stools under it, 23") clear of the
 * walkway corridors — gating corridors on the 36" clearance zone instead
 * seated nobody in the whole fixture corpus. An island seats the side its
 * sink backs onto (the non-cook side) and a peninsula its dining side, bridging
 * its appliances (a seat behind the cooktop is fine). A seated edge must hold
 * at least 2 stools, and AT MOST ONE leg of a multi-arm peninsula is seated.
 * A peninsula's overhang is its own slab spanning the leg out to the corner
 * (a per-run overhang stops 24" short, because the perpendicular wall owns
 * the corner box), trimmed back wherever
 * reaching the wall end would enter a walkway. Island seating proposed by
 * `islandPlacement` is re-probed in validate() against the candidate's own
 * cabinets, since enumeration measures an empty kitchen. New objects +
 * countertop dimensions, so cached layouts are stale.
 * 1.46.0: per-typology clearance tiering, island-only `+island` typology
 * cards, and the fridge-column materialize cap — all change generated
 * layouts. (Bumped in 33b99a998 without a note here; recorded on the
 * rebase that added 1.47.0.)
 * 1.45.0: the U rules are now shared by every path that
 * builds a U (`uShape.js`), not just the floating-U family — so an
 * `L+peninsula` U gets the 48"/36" interior minimums AND a flush-mouth
 * aligned-arm variant whose tip lands level with the opposing run's open end
 * (31B79781's winner went from arms staggered 35.8" to flush, 84" → 120").
 * Separately, the circulation gate now sizes a walled-off pocket by its FLOOR
 * area rather than the minWidth-eroded navigable core, so a sealed-off U
 * interior can no longer collect the BEST clearance grade (43.6 sq ft was
 * measuring 6.8); run-end slots re-anchor to the usable span when a corner
 * reservation trims the wall end, instead of being dropped; and sub-cabinet
 * slack is packed into that reservation rather than stranded mid-run as a
 * blank panel. Changes which layouts exist AND their ranking, so cached
 * layouts are stale.
 * 1.44.0: the fridge now TERMINATES a run at an OPEN end.
 * A fill-time pre-pass (`endpointSnap.seatFridgeAtOpenRunEnd`, run before
 * scoring) reseats each fridge flush at its run's open end — the reachable one
 * when a single run has both ends open — and the `fridgeRunEnd` (+5) rule is now
 * purely geometric: it credits the seated position, not the enumerated slot
 * kind, and never a detached fridge. Its inside-corner withhold only fires when
 * the perpendicular leg is a real cabinet wall. Separately, diagonal
 * (non-right-angle) corners fill the sub-cabinet residual with an asymmetric
 * corner-fill pentagon so the counter no longer breaks, and the half-baked
 * aligned-short floating-U fridge-mouth variant was removed. Changes fridge +
 * cabinet positions AND which layouts rank, so cached layouts are stale.
 * 1.41.0: split-L suppression — an L-shape candidate whose
 * two legs don't meet at the corner (a "split L": an appliance in/beside the
 * inside corner, or an opening that shortens a leg) is now hidden from the
 * L-shape typology whenever any continuous L exists for the kitchen. Split Ls
 * survive only when there is no continuous option (better than an empty
 * section). Classification is world-space (`cornerOutcome.classifyLCorner`) on
 * the materialized geometry. Changes which L-shape layouts surface, so cached
 * layouts are stale.
 * 1.40.0: the fridgeRunEnd (+5) and dishwasherSide (+4)
 * scoring rules are now corner-aware — both rewarded "appliance at a wall end",
 * which is geometrically the same slot as "inside corner of an L". An appliance
 * flush at an inside corner makes cornerDetection suppress the corner cabinet
 * and blank `depth + 30"` of the perpendicular leg, so the rules were paying for
 * the configuration that breaks the run. The reward is now withheld when the
 * perpendicular leg carries its own run, and kept when it can't (stub / door /
 * open end). Changes candidate ranking, so cached layouts are stale.
 * 1.39.0: endpoint-snap gains two more anchors — an
 * opposing wall's APPLIANCE edges (a facing run terminating with an appliance)
 * and, for uppers, the same wall's already-snapped LOWER run-ends (uppers follow
 * the lowers beneath them). 1.38.0: cosmetic endpoint-snap — a cabinet/upper run's
 * terminal edge (or the appliance a run terminates with) is nudged up to 2"
 * onto a nearby alignment anchor (opposing-wall opening edge, island end edge,
 * or the opposing U-arm's run endpoint) when already that close; reachability-
 * bounded so it never gaps an appliance or grows into a walkway. See
 * shared-lib/kitchen-assembly/endpointSnap.js. 1.37.0: auto-kitchen cabinet output is now RICHER and
 * EDITABLE. Every lower pass (straight fill, notch-patch, sink, shallow run)
 * and the upper pass emit <=36" typed MODULE BAYS with bay types (drawer /
 * drawer-doors / sink / standard) + false-panel fillers instead of one coarse
 * box per span (shared shared-lib/kitchen-assembly/typedBayFill.js). Apply also
 * derives editable single-wall `room.cabinetRuns[]` and tags the emitted
 * bay objects with `runId` + `layoutProvenance`, so auto kitchens open in the
 * manual run editor (per-bay width/type + run/unit appearance). Object shapes
 * changed → cached layouts must invalidate. (The sink basin/faucet builder was
 * also folded onto the shared buildKitchenSink.js, but that is byte-identical.)
 * 1.36.0: the detached-appliance (fridge) pass now
 * subtracts fridge-blocking / low-sill windows from its standalone-spot
 * walls, so a tall detached fridge is no longer tucked in front of a window
 * (its body would occlude it). The inline enumerator already forbade this,
 * but a standalone spot on a non-run wall bypassed that filter. The
 * window-blocking predicates (applianceBlocksWindow / isLowSillWindow) are
 * extracted to a shared candidates/windowBlocking.js so both paths agree.
 * 1.35.0: an end-of-flow pass adds storage-only
 * shallow (~12") cabinet runs on the free, corridor-pinched sub-segments
 * of any wall — including a wall that already carries the deep run on
 * another stretch — where a full 24" run won't fit (a walkway corridor
 * pinches it to 10–22") but a slim run still leaves a >=36" walkway.
 * Countertop depth is now per-run, so a wall mixing a deep and a shallow
 * run gets a 25" slab over the deep part and a 13" slab over the shallow
 * part. 1.34.0: cross-room walkway MST splices a "bridge"
 * connector at an interior opening between two contributing rooms ONLY when it
 * reconnects an otherwise-orphaned region — so a room reachable only through a
 * narrow, offset passage (e.g. a second kitchen) stays in the circulation MST
 * instead of being silently dropped; passages already joined via a wider gap
 * add no node, so walkways aren't over-constrained. Single-room kitchens
 * unaffected (their openings sit on the perimeter). 1.33.0: the sink-housing base cabinet widened from
 * 30" to a standard 36" sink base (shared KITCHEN_SINK_BASE_WIDTH), on both
 * the island (buildIslandWithSink) and wall (applianceRequirements) paths,
 * and the island wet-zone offset now derives from it (DW 24" + sink-base/2 =
 * 42", was 39"). A 30" base capped sink-model selection below every real
 * catalog sink (smallest single-bowl is 32"), so styling only ever picked a
 * 15" bar sink; a 36" base admits the 32"/35" sinks. 1.32.0: the island
 * dishwasher now faces the cook
 * (walkway) side like the front cabinets / sink / faucet — it carried the bare
 * island angle (ry) instead of frontRotation(ry, forwardSign), so whenever the
 * cook side resolved to the negative-axis2 edge the DW door faced backward into
 * the back-of-DW cabinet. 1.31.0: the island validate path now carries the
 * detached appliance's rect onto the returned `envelopeRects` (it recomputes a
 * cabinet-walls-only island envelope, which omitted the corner fridge). The
 * preview thumbnail (renderLayoutSvg reads envelopeRects) was missing the
 * detached fridge for island layouts even though apply (materializedObjects)
 * placed it correctly — preview now matches apply. 1.30.0: detached placements carry `height` so
 * materializeCandidate's centerY (floorY + height/2) and dimensions.y are real
 * numbers — without it the detached appliance object had center.y = NaN, which
 * passed generation/scoring but failed the Firestore JSON encode on apply
 * ("Data cannot be encoded in JSON: NaN"). Version bump invalidates stale
 * cached layouts whose detached placements/materializedObjects predate the fix.
 * 1.29.0: detached-appliance spot selection is now
 * per-walkway-variant (corridor coverage of a corner differs by routing, so
 * the global up-front cap was dropping corners that were clear in some
 * variants), AND the relief+detach combo is enumerated DIRECTLY (range-only
 * wall run + corner fridge + island-bound sink) instead of being derived from
 * a relief candidate that required the fridge on the wall first — so a
 * fragmented merged wall whose only corridor-free fridge corner lives in a
 * routing where the fridge can't share the wall now yields range-on-wall +
 * fridge-in-corner + sink-on-island. 11/12 fixture kitchens now produce
 * detached layouts (was 8/12). 1.28.0: the detached appliance's envelope rect now
 * carries its wallId, so validate()'s opening-clearance check skips openings on
 * the fridge's OWN wall (the same skip inline appliances get). Without it every
 * detached fridge was rejected against its own wall's door/window, so no
 * detached layout ever surfaced; with it, 8/12 fixture kitchens now produce
 * detached-fridge layouts (tight cross-room kitchens whose perimeter is all
 * circulation + openings still correctly produce none). 1.27.0: a detached
 * appliance's footprint is appended to
 * the candidate envelope, so it's both drawn in the layout SVG (renderLayoutSvg
 * reads envelopeRects) and opening-clearance-checked — previously the detached
 * fridge was placed + materialized but invisible in 2D and skipped the
 * perpendicular-opening check. Now that it's properly scored, a far detached
 * corner correctly loses to an inline fridge. 1.26.0: detached-appliance
 * placement now carries
 * wallParam/width/depth so the corridor-overlap check builds a real footprint
 * — previously it spuriously rejected every detached fridge in any kitchen with
 * corridors (and only "worked" for corridor-less kitchens); plus a cross-room
 * combo that offloads BOTH sink+DW (to the island) and the fridge (to a corner)
 * so a tight merged-region wall can host just the range. 1.25.0: per-typology
 * "detached appliance" pass — a
 * detachable appliance (fridge; wall ovens later) is also enumerated on a
 * standalone wall/corner off the cabinet run, so a kitchen with a spare corner
 * can free the run; corner-ranked + capped, additive (scoring picks inline vs
 * detached). 1.24.0: cross-room/merged kitchens get sink-on-island
 * relief — island typologies also enumerate range+fridge-only wall candidates
 * and mount the sink+DW on the island, instead of requiring a full
 * sink-on-wall run first; fixes "no feasible" where the merged region's
 * cabinet wall is fragmented by the through-gap corridor + the demoed divider.
 * Single-room kitchens unaffected (gated on compoundFloorPolygon). 1.23.0:
 * cross-room walkway domain joins contributing
 * rooms only at the openings/doors that actually connect them — rooms with
 * no shared opening don't merge — so the corridor MST routes through real
 * passages, not across solid walls. 1.22.0: cross-room kitchens compute
 * walkways over the union of contributing-room floors (compoundFloorPolygon),
 * so the adjoining rooms' exits drive the corridor MST and through-circulation
 * is constrained — previously a cross-room region produced no corridors.
 * 1.21.0: island hugs the cabinet run — in an
 * open/cross-room region it sits one preferred aisle off the run instead
 * of centering in the whole free band; galleys/normal kitchens unchanged.
 * 1.65.0: the derived run model carries what the generator decided (batch 3
 * of the kitchen editing review — #1717, #1719, #1722, #1724). A segment's
 * extent is the span of the emitted LOWER cabinetry, not the whole wall, so the
 * first edit no longer fills the corridor, island reserve or wall-body blocker
 * the layout stopped short of; a corner body the run cannot rebuild — a
 * single-sided unit (one wall ref, never run-owned), a kite, the L of a
 * rejected join — is reserved as a hard `corner` bay; a gap filler or appliance
 * end piece carries `category.filler` and is run-owned, so the re-solve replaces
 * it instead of stacking its own beside it; a wall oven reserves its column as
 * an anchored `tallUnit`; and bay anchors come from the SNAPPED placements the
 * boxes were built from. No layout projection moves (the corpus re-cuts
 * byte-identical) — what changes is the first EDIT of a generated kitchen,
 * which now reproduces the applied geometry.
 * 1.63.0: a placed body's footprint stops being mirrored. The peninsula,
 * island-obstacle, corridor-overlap, aisle and barstool helpers passed a
 * stored `rotation.y` to `calculateCorners`, which reflects the rectangle on
 * any non-axis angle; they now use `shared-lib/geometry/objectFootprint`.
 * Measured on the corpus, isolated against a clean main (the committed
 * layout-kitchen baseline was ALREADY stale, so the raw test diff overstates
 * this): a Pittsburgh kitchen loses island placements — single-wall+island
 * 8 -> 5, U+island 2 -> 1, topScores 78 -> 73 and 74.5 -> 69.5 — because the
 * islands had been sitting on obstacles the mirror moved out of the way, and
 * the aisle is now measured against the island's real footprint. Nothing else
 * in the projection moves.
 * 1.20.0: run-aligned island orientation now survives end-to-end —
 * enumerateIslands orders run-aligned options first and the earlyExit
 * fitted path tries the run-aligned orientation first, so the rendered
 * island parallels the cabinet run. 1.19.0: island clears interior walls +
 * initial run-aligned island sort for cross-room kitchens. 1.62.0: a
 * peninsula set back for seating reserves the band it left on its anchor
 * wall, persists `seatingSetback`, is dropped when unseated and never folds
 * with its flush twin; the floating-U walks the whole anchor-offset
 * ladder. 1.64.0: island sizes are a frontier — one fitted candidate per
 * non-dominated (length, depth, aisle grade), a phase-1 reserve per island
 * kind and size, one top-K slot per island size, bare vs sink-on-island
 * never fold; a sink on the applied island is kept (fixed island with
 * sink, dishwasher and stools); deep-island stools tuck into the carved
 * knee. 1.66.0: a peninsula is offered whole or not at all — the walkway
 * gate rejects an end-trim that would move an L junction or leave an arm
 * under 60", instead of letting the fill stop short of the corner and ship
 * the far arm as a floating island (Scan 4: 762 -> 415 peninsula layouts;
 * only floating-U / single-wall+peninsula stubs go elsewhere). 1.67.0: a
 * walkway band that laps a peninsula's flush root offers an anchor slide
 * past it, one per routing, as an alternative variant (`+5"`, `+23"` …),
 * and the floating-U rung walk runs the caller's gates inside it — so a
 * peninsula the walkway would have cut off moves instead of being dropped,
 * and a wall end whose only obstacle is a walkway (no door zone) can host
 * one at all. Scan 4: 415 -> 3167 peninsula layouts, the room's first
 * south-side peninsulas; B06B9943 +197; generation time ~2x.) 1.68.0: derived
 * runs store params on the raw room wall; the kitchen clip is remapped away at
 * derive time (toRuns.js). 1.69.0: a sliding / pocket door reserves the 30"
 * passage rather than a swing zone as deep as it is wide, so a wide patio
 * slider stops pushing a peninsula's anchor to mid-wall (Golden West: 223" ->
 * 57" on a 337" wall); and a floating-U's arms STATE which face their cabinets
 * stand on instead of leaving it to the cook-side heuristic, which reversed an
 * arm anchored past the wall's midpoint (7 of 124 corpus arms -> 0). Persisted
 * peninsula `workingNormal`s are now perpendicular to their own arm; every one
 * before this was 46.2° off. 1.73.0: an appliance seated exactly one cabinet
 * depth from a corner no longer claims it (float noise had it reserving 54" of
 * the perpendicular wall behind a blank panel); a narrow span needs a body at
 * ONE end, not both, so the 27" run between a range and a doorway and the 9"
 * pull-out beside a fridge are built as the run engine builds them; a fridge
 * parked at the corner of a run-less wall is a body the run stops for, at a
 * stop that clears its footprint (225 corpus layouts had cabinets inside the
 * fridge); the endpoint snap no longer grows an end filler across a skewed
 * facing wall. 1.74.0: the layout persists what the corner loop DECIDED at
 * every corner (`corners`), in the run engine's treatment vocabulary, and the
 * derived run takes that ahead of reading the objects; the blank box a true
 * blind corner leaves is its own word (`blank`), so the first edit no longer
 * packs a cabinet into it. 1.75.0: the generator's fill is EXECUTED BY THE
 * RUN ENGINE (`engineFill.js`) — the objects a layout materializes are the
 * engine's execution of the generator's decisions, so the first edit changes
 * nothing; the derived appliance anchor carries the sink's group. 1.76.0: the
 * generator's own packer is retired — its plan's spans (`built`/`bodies`) set
 * the run's extent, the engine builds every box, the endpoint snap's
 * decision is honoured (with the vacated strip covered where the packer's
 * filler rule anchors it), a blank blind box has its counter, a peninsula's
 * seating no longer hinges on float noise at the arm's end, and an arm whose
 * seating strip lies across a low-sill window loses the seat to the other
 * arm instead of taking it and being refused. 1.77.0: the SCORER asks the
 * appliance-at-corner question the way 1.73.0 taught the classifier and the
 * run engine to — `counterSpace`, `analyzeWallAt` and
 * `applianceEndsRunAt` share `cornerProximity.claimsCorner`, so a layout is no
 * longer ranked for counter an exactly-24" appliance was never going to take
 * away. Parity corpus: 5 of 12 kitchens re-rank, every flipped decision within
 * 1.3e-15 m of 24"; scores only rise (1CFF46CC 76.3 -> 80.3, B06B9943 61.1 ->
 * 67.6, F6D83996 48 -> 50, E5DED26F gains a 57). 1.78.0: a peninsula's back panel records only its bridge to the corner box
 * into the plan's spans, so a strip the endpoint snap vacated at the arm's tip
 * stays out of the run's extent and the first edit keeps the cover.
 * 1.79.0: the corner pre-pass bounds a sink's slide by the wall's other corner
 * reservations, so the corner yields instead of pushing the sink and its
 * dishwasher into a peninsula root's footprint.
 * 1.80.0: an inside corner gets a 3" reveal — the enumeration reserves a depth
 * plus the reveal at every template corner, the pre-pass keeps a sliding sink
 * out of it, and the engine builds it as a filler on the leg a blind or blank
 * box trims, so the first door there clears the perpendicular unit's handles.
 * 1.81.0: the peninsula arm ladder is 63/75/87/99/120 — the 12" rungs plus the
 * inside-corner reveal — so an arm packs its unit, the reveal and its cabinets
 * without the reveal costing the last one (user ruling 2026-09-15).
 * 1.82.0: a walkway trim at an arm's bottomLeft rebases the placements on it,
 * so its appliances stay where they were enumerated instead of sliding toward
 * the root; a trim that would cut through an appliance rejects the layout.
 * 1.83.0: a new layout inherits none of the PREVIOUS one's links (#2176). The
 * engine fills against the room, and the room still holds the last layout's
 * `align-end` links — a run keeps its id across a regeneration
 * (`autorun_<wallId>`), so they still name it, and `applyLinkAnchors` sets a
 * segment end outright with no bound and nothing in `unsatisfied`. A 2"
 * cosmetic `endpointSnap` nudge became a 64" haul on New Montrose / Scan 10,
 * dropping 2 of its 4 top-ranked layouts' outer bays (33-63.5" of base run)
 * while `cabinetWalls[].bodies` still listed them. `executeFillThroughEngine`
 * now replaces the room's links with the layout's own before filling, the way
 * `applyCabinetLayout` already did on the way to Firestore. BOTH sites replace
 * the WHOLE room-level set, user-authored links included (user ruling
 * 2026-09-19): such a link names a run end, a bay, an opening or a wall — the
 * cabinetry this layout is replacing — and inherits the same unbounded haul.
 * Measured over the 8 dev rooms holding a stored link, 8 of 64 generated
 * layouts were decided by it, and not always downward (New Montrose -171.4",
 * Brown +46.5", Tasoro +30.5"). All 8 rooms' links were the generator's, so
 * nothing user-authored existed to lose; the emulator corpus carries none, so
 * corpus output is unchanged. THREE sites, not two: the minimal-change option
 * (`web-app/src/services/kitchen/minimalChangeLayout.js`) never goes through
 * `executeFillThroughEngine` — it re-solves the room's own runs through
 * `materializeRunObjects`, which falls back to `runLinksIn` — so it kept
 * inheriting them after the other two were fixed, on runs a previous generated
 * apply named (`claimsRun` is source-agnostic). One planted link cost it 5 of
 * 11 base cabinets on Anchorage 2 / Scan 1.
 * 1.84.0: the upper tier plans its own corners
 * (claude-docs/2026-09-19-upper-cabinet-tier-design.md) — the upper L is the 24" standard wall corner on
 * per-leg reaches and hangs over any lower corner, no upper is wider than 36",
 * a clipped sliver merges into its neighbour, openings block uppers by height —
 * and a corner cabinet a walkway rules out no longer leaves a hole: the clear
 * wall's run goes straight to the corner, with no upper L over a corner that
 * has no lower L (user rulings 2026-09-19). And a new layout hangs its uppers
 * WHERE THE ROOM ALREADY HANGS THEM: the generator reads the spec the room's
 * runs agree on (`roomUpperSpec` — to the ceiling, or a gap over the counter,
 * at a height), builds to that band, and stamps it on the derived runs, so the
 * preview, the apply and the first edit agree. A room that states nothing is
 * byte-identical. The wall hood rises to the same line (its 24" WAS the
 * standard line, written as a constant). And the fridge's footprint is its
 * COLUMN — the box plus the surround's ½" panels: it stands a panel's width
 * inside its segment (52 of 111 fixture layouts had a panel inside the side
 * wall), no appliance is placed against its box, and the run engine seats it
 * with the stretch's remainder on the WALL side rather than between the fridge
 * and the cabinets (user request 2026-09-20) — never less than 3" against a
 * side wall, the room its door needs, and at an OPEN run end no filler at all:
 * the fridge comes in by the remainder (user rulings 2026-09-20). And a window
 * the fridge would occlude limits where it may STAND without ending the run:
 * the run-end seat and the `fridgeRunEnd` credit read one pair of segments
 * (`tallApplianceRunSegments`), so a fridge between a window and a doorway
 * ends the run at the doorway instead of being left mid-run as "ambiguous"
 * (2 of 111 fixture layouts change).
 * 1.85.0: no appliance is seated without a 36" walkway in front of it
 * (`frontClearanceGate`), measured against the room's own floor — the only
 * thing that sees a doorless dead-end alcove, which the walkway corridors never
 * enter. Both enumeration paths (the dishwasher has its own), run packing,
 * standalone fridge spots and the fridge seat pass all refuse a crowded seat; a
 * typology whose EVERY seat is crowded reruns ungated rather than empty. A 30"
 * fridge had been seated in a 40.5"-deep alcove with 10.5" in front (Pittsburgh
 * Full Alexa Scan) — outscoring its own roomy siblings, since `fridgeRunEnd`
 * paid for the "open" run end — and with the fridge alone refused, the sink and
 * dishwasher took the alcove instead.
 * 1.86.0: the cabinet run itself stops at a dead end too shallow to use — a
 * stretch leaving a base cabinet under 21" of floor (`APPLIANCE_FRONT_CLEARANCE`,
 * NKBA's standing space; use clearance, since a dead end is not a passage and
 * the walkway corridors already govern passages). Reserved in
 * `wallBlockedRangesById` at generation and against the fill at
 * materialization, and persisted as clearance. With 1.85.0's appliances kept
 * out, base cabinets still filled the same alcove, 17" from its facing wall.
 *
 * 1.87.0: every upper cabinet is a width its run's FAMILY sells. A mirrored
 * upper a keep-out trims (a window's buffer, the hood's sky, what a corner
 * took) used to be whatever wall was left — a 36" bay beside a window hung a
 * 32¼" upper under a Stock family (user report 2026-09-21) — and a clipped
 * sliver merged into its neighbour at any width up to 36" (24" + 1" = 25").
 * Now the trimmed box is ONE cabinet, the widest the family sells that fits,
 * standing against the side that was not cut so the tiers still line up at the
 * lower's seam; what is left beside an opening or the hood stays BARE and only
 * a gap against a body is scribed; and a sliver merges only into a width the
 * family sells (user rulings 2026-09-21). Over the 111 generated fixture
 * layouts, on the default 3" grid: off-grid plain uppers 97 of 699 → 0, in 78
 * layouts; the same number of uppers, no other object moved, no collision
 * added. A Custom (continuous) family sells any width and is untouched.
 *
 * 1.88.0: two scoring rulings (2026-09-21). The aisle penalty starts below
 * NKBA's 42" ONE-cook work aisle, not 48"; islands are graded preferred ≥48 /
 * comfortable 42–48 / minimum 36–42 so the scorer can tell them apart. And a
 * fridge ending its run against a side wall — 3.5" off it, its column's panel
 * plus door-swing filler — earns `fridgeRunEnd` again (`fridgeColumnInset`).
 *
 * 1.89.0: the kitchen walkway network crosses each border between two
 * neighbouring regions ONCE, however many segments it is made of
 * (`regionPairBorders`, user ruling 2026-09-21). #2206 made a drawn kitchen
 * area a region, and counting straight edges turned its outline into many
 * mandatory crossings — Seward Dev Scan 4 → 12, one pinning a corridor at a
 * stub wall's tip.
 *
 * 1.90.0: square or oblique is ONE rule, 5° off a right angle
 * (`cornerDetection.SQUARE_CORNER_TOL_DEG`, user ruling 2026-09-28), shared
 * by the corner classes, the shape graph and the run engine. The generator
 * held 11.5° and the engine 15°: a 104.7° corner was planned and recorded as
 * a kite, and Apply's re-solve built a square L that could not fit (prod
 * Pittsburgh Scan 2). Corners 5-11.5° off square now get the kite too. No
 * corpus kitchen has one: 998 layouts of the four structures with a corner in
 * the 5-15° band regenerate identically, and the nightly kitchen suites are
 * unchanged.
 *
 * 1.91.0: an island sink faces the WORK TRIANGLE (user ruling 2026-09-30,
 * `islandCookSide.js`): the long face the range / fridge / wall oven stand
 * across, and on a tie the run of cabinets `islandPlacement` faced it at. The
 * appliance centroid used to decide, and on Golden West, Temple City / Scan 1
 * it turned the sink to open floor on 9.7" of offset from appliances past the
 * island's END. The run side is read from `sinkSlot.facing`: its `backSide`
 * label belongs to `islandPlacement`'s own axis2, and read against the
 * rotation's axis2 it pointed the other way. 31 of 18,314 corpus layouts
 * change, all island layouts in six structures.
 *
 * 1.92.0: a REFUSED leg gets no upper L. `cornerDetection.refuseLeg` (2026-09-27)
 * decides that a vertex whose short leg cannot hold a cabinet is not a corner —
 * the long wall runs to it plainly and the short leg is reserved as floor — but
 * the state it carries, `wallA-only` / `wallB-only`, is shared with the blind
 * box, where both walls DO carry runs, and the upper L was emitted on the state
 * name alone. Its second leg then stood over the refused leg's bare floor, and
 * the surviving wall's 36" straight upper — the run engine's, which sees no
 * corner where a run simply ends — hung straight through it (room 3B76932B of
 * A3EC02AF and of its `_v1` variant, 5 layouts and 4 x 3 bands, caught by
 * `generatedUpperTier.nightly` on the
 * 2026-09-29 run and now pinned at the PR gate by
 * `cornerRefusedLegUpper.ci`). The answer is the walkway branch's, unchanged
 * since the 2026-09-19 ruling: an ordinary run end with straight uppers over it.
 *
 * 1.93.0: an island sink sits toward the range / fridge end with a sink
 * landing (18") before that end, and the dishwasher on its FAR side (user
 * ruling 2026-10-06, `islandCookSide.islandSinkAlong`). Main already landed
 * there by accident: a stale 39" slot made the pair overflow the island's end
 * and the builder moved the dishwasher across the sink. The builder now keeps
 * the side it is given and slides the pair to fit. It takes the sink cabinet's
 * width, and a kept island keeps its own. `centeredOnIsland` became
 * `islandSinkLanding`, which no longer favours one island length. Corpus: 169
 * of 18,319 layouts change, all island layouts in 9 scans; 20 of 23 top
 * island-sink picks keep their island and 3 take a longer one that holds the
 * sink's counter (914A30D1 60" -> 120", 66616D52 66" -> 90", BE8E1430 78" ->
 * 90"). Golden West's stays 78".
 *
 * 1.94.0: a work-triangle leg that passes through a WALL forfeits the
 * triangle's 10 points and costs 10 more per blocked leg (user ruling 2026-10-07, `workTriangleWalls.js`). Legs
 * run between the standing points 21" in front of each appliance, against the
 * whole room's real walls; doors and openings pass. Seward Dev Scan / Scan 1's
 * island sink outside the alcove its range and fridge stand in ranked 8th
 * (at 1.93.0); its best blocked layout now ranks 312th, none in the top 100,
 * top layout unchanged. Corpus: 228 of 18,345 layouts change, all in Seward
 * (227) and Dako (1), the only kitchens with a wall in the triangle.
 *
 * 1.95.0: a corner is planned only as a unit the cabinet LINE sells
 * (`cornerDetection.classifyCorner`'s `units`). Venus's 33" L (LSB33) beside
 * the 36"; a blind unit only on a leg the line's shortest blind base fits (39"
 * in Venus); and a corner nothing fits is a dead corner under a continuous
 * counter, never skipped. Golden West / Scan 1 under Venus had shipped a bare
 * hole in the countertop at its vertex. Over the corpus's 30 kitchens (L and U,
 * 900 layouts a side): no layout without a line changes; under Venus 50 do —
 * 42 blind units become LSB33s, and the 10 corners the old build skipped get a
 * dead corner (7) or are left empty for a walkway (3). The Venus wide-filler
 * ranking now asks the room the caller builds from (`roomData`), not the
 * clipped kitchen walls.
 *
 * 1.96.0: the narrowest filler a run emits is ½" (`MIN_FILLER_IN`, was ¼"),
 * and the generator's snap cover and endpoint-snap drop-guard read it; a
 * leftover under ½" is left to scribe.
 *
 * 1.97.0: where a run dies into a real wall over that scribe, the countertop
 * runs on to the wall (`cabinet-runs/scribeCounter.js`) and records the point
 * (`scribeTo`) so the post-processing resync keeps it. Never at an island or
 * peninsula face (a virtual wall); vanity tops included.
 *
 * 1.98.0: a corner refuses its short leg against 36" in every line (a line's
 * longer blind base only decides whether a single-sided unit can be built),
 * and when both legs are under 36" a leg with nothing standing in front of the
 * corner box is refused instead of reserving a blind box, so the other wall's
 * run reaches the corner with a base and its countertop. Over the corpus's 24
 * kitchens (top 40 each): 3 layouts change, the same in every line.
 *
 * 1.99.0: a dishwasher end panel takes its run's look (`fillRun`), so it is
 * drawn flat in the run's colour and a vendor line prices it (Venus DWR in
 * the run's colour instead of an unpriced "no colour" line).
 *
 * 1.100.0: a seated peninsula is topped by ONE slab from its free end to the
 * face it meets at the root — cook-side overhang, cabinets, back panel and
 * the 12" knee overhang — not a run slab plus a knee strip plus the crossing
 * run's slab; a crossing top stops at its cook-side edge, a range cuts it
 * with a bar behind the range, and the arm's cabinet wall keeps the seated
 * span (`seating`) so a re-solve rebuilds it (`seatedPeninsulaTop.js`). A run
 * dying into a peninsula mid-way along a real wall now covers the back panel.
 * Over the corpus's 25 kitchens (top 3 per peninsula typology, 63 layouts):
 * 31 seated arms — 23 one slab, 8 cut at a range — and the bare 1" × 24"
 * strip of panel goes from 23 layouts to none.
 *
 * 1.101.0: an island's or a peninsula's finished back wears the cabinetry's
 * look — a peninsula's its arm cabinets', an island's the line's — carries the
 * line as `runFamily` and is marked `category.backPanel`, so under Venus it
 * bills as BP sheets, one per started 96". The generator's dishwasher end
 * panel at a peninsula's tip takes the arm's look too, drawn flat. Every
 * generated peninsula and shallow island back panel, and that end panel,
 * changes; nothing else does.
 *
 * 1.102.0: a dishwasher at a run's end is closed wherever there is room.
 * An island's gets the END PIECE a wall run's gets — 4¼" (2x4 blocking laid
 * flat + ¾" panel, `applianceEndPanel.js`), or what the island has left —
 * instead of a ½" panel; the sink + DW pair slides inward for it
 * (`buildIslandWithSink`; Golden West / Scan 1). The engine's inset slides the
 * dishwasher's whole row of butted appliances into the free run, down to the
 * 2¼" minimum, where it used to put the dishwasher 4¼" inside the sink base
 * beside it; a strip it keeps back is the piece's whole; and at a Venus butt
 * joint the sliver to the bisector is the dishwasher's end piece instead of
 * open wedge (`fillRun.endPieceBounds`; 19 Rapley Close). Corpus (30
 * kitchens, L/U/galley/single-wall and the island typologies, 3,799 layouts,
 * measured against 1.96.0): 173 change without a line and 176 under Venus —
 * ~78 sink islands a side (66"–78", the counter beside the sink 3¾" shorter),
 * 36 pieces widened to their whole strip, 25 dishwasher/sink-base overlaps
 * resolved by the slide, 5 Venus butt joints. And an oblique corner's kite
 * whose legs stop at different distances no longer crosses itself with a
 * corner a cabinet depth beyond the wall (`buildObliqueKite` built its second
 * leg facing out of the room): 104 of 312 corpus pieces, 0 now. Bare
 * dishwasher sides 81 → 12 (Venus 85 → 12); the rest are mostly the 44
 * islands exactly the pair long (60"), which lose the ½" panel that hung past
 * the island — nothing fits.
 *
 * 1.103.0: a dishwasher whose strip to its run's end is under the 2¼" end-piece
 * minimum slides, with the row butted against it, until the piece is a real one
 * — 4¼" where the run has room (`fillRun.endPieceBounds`). The engine runs this
 * when a layout is materialized: over the corpus (30 kitchens, every variant,
 * 14,871 layouts) 387 change, end pieces under 2¼" go from 686 to 304, the sink
 * beside the dishwasher moves 2–4" (median 3¼") and nothing new overlaps. And a
 * re-solved island's dishwasher, sink and faucet follow that slide — they stood
 * still and the panel landed inside the dishwasher.
 *
 * 1.104.0: a corner leg of EXACTLY a unit's length takes it (`legTakes`, one
 * comparison for the classifier and the run engine) — an appliance seated
 * flush against a corner reservation leaves a leg of exactly 36", and float
 * noise used to decide whether that leg got the L. Over the 9 processed
 * fixtures (2,825 layouts, 2,799 under Venus) 108 change (128 under Venus),
 * all in one kitchen, B06B9943: a 48" blind unit, its 3" filler and the base
 * beside it become a 36" L corner cabinet and one base, the uppers following.
 * And a doorway wholly behind a mid-wall junction no longer counts as standing
 * in the corner box (`nearestBlockingOpeningAt`).
 *
 * 1.105.0: a sink the plan centres on a window is credited (`sinkOnWindow`,
 * +10) and linked to the window only while the BUILT kitchen keeps it there.
 * Clearing a corner (`clearCornersOfSinks`) or a dishwasher's end piece can
 * slide it off; 132 of the corpus's 2,007 window-centred layouts (30 kitchens,
 * every variant) kept the points and a link for a sink 4–9" away — now none
 * does, and all 1,875 that stay keep both. The top option changes in 2 of 30
 * kitchens.
 *
 * 1.106.0: a dishwasher's end piece where the kitchen is built, not where the
 * engine finds room for it later. A dishwasher at a peninsula's free tip has
 * its row slide toward the root before the corner is decided and the corner
 * yields (`armTipEndPiece.js`, user decision 2026-10-21); the end-panel planner
 * measures a wall by its axes (a peninsula's virtual wall has no `dimensions`,
 * a clipped wall carries them unclipped), so the strip beside a dishwasher is
 * planned as its piece where it stands; and the engine slides no dishwasher off
 * a bound its corner already closes (an L unit, a blind unit, a reveal
 * filler). Corpus (30 kitchens, every variant, 14,865 layouts): 1,030 change,
 * 808 keep their dishwasher where the plan put it, end pieces 2,928 → 2,735,
 * bare dishwasher sides 373 → 27, no new overlaps, and the first edit moves no
 * appliance (`runFillInvariants.nightly`: 54 moves → 0).
 *
 * 1.107.0: a generated island's sink base is the widest the line sells, up to
 * 36", that leaves its dishwasher the 2¼" minimum end piece
 * (`islandSinkBase.js`, user ruling 2026-10-21) — a 60" island's 36" base left
 * the dishwasher bare at the island's end; it is now 33" with a 3" piece (30"
 * with the full piece in a line with no 33"), and an island too short for any
 * base gets no sink. The placeholder basin fits its base, the island's
 * dishwasher end piece wears the line (Venus prices it as its dishwasher
 * return), and the island's run reserves its sink bay at the sink cabinet's
 * width, not the basin's (the first edit rebuilt a 36" base at 30"). Corpus
 * (30 kitchens, every variant): bare island dishwasher sides 18 → 0 (Venus
 * 20 → 0, Generic Stock 18 → 0), no overlaps, the top option unchanged in all
 * 30 kitchens.
 *
 * 1.108.0: applying option #1 (the kitchen there, rebuilt) keeps its island
 * and peninsula runs. The apply works wall runs out again from
 * `_cabinetWallIds` and an island's from the generator's `category.island`
 * objects, and neither reaches what option #1 hands it: it saved an island as
 * a 36" run holding its sink base alone and dropped a peninsula's run. The
 * layout now carries those runs (`_carriedRuns`) and the apply saves them as
 * they are, once (`ownership.runsAfterApply`). Corpus (28 kitchens): 114
 * pieces in 14 kitchens left owned by no run, 10 of them pieces that had one
 * → 6, each the generator's own fridge surround, which a generated apply
 * leaves outside its runs too.
 *
 * 1.109.0: a generated kitchen's fridge column and hood, built by the run
 * engine, follow the rules `style_kitchen` now shares (user rulings
 * 2026-10-21/22). The column is 24" deep for a fridge up to 31" — the 30"
 * default and every catalog fridge — and 30" for a deeper one, where it was
 * as deep as the fridge's box; the cabinet over the fridge is never under 12"
 * in a line with no size catalog; and a range's hood hangs from the counter,
 * not its box. Option #1 keeps a tall filler at its column's depth.
 *
 * 1.110.0: a generated island's and peninsula's back is the run engine's.
 * Every back panel faces out, away from its cabinets (all 9,900 peninsula
 * panels in the corpus pool faced in); a plain island seated on its +axis2
 * side is no longer built inside out (110 of 524 island layouts, which the
 * first edit then rebuilt 12"-18" off); an island with no back row gets the
 * engine's 1" door-style back, a seated 36" one included; and the 30"/36"/42"
 * island depths, which the depth sweep reaches a float's width under, split
 * as the boundary — a 36" island now carves its knee. Top option unchanged
 * in all 30 corpus kitchens.
 *
 * 1.111.0: an island's and a peninsula's back is drawn as the flat finished
 * sheet it is priced and sold as (Venus's back-panel sheet, a generic panel):
 * the island's no longer carries `decorativeFront`, whose row of door fronts
 * nobody priced, and the renderer no longer reads the flag on saved backs.
 *
 * 1.112.0: a generated island is built by the run engine, the way its first
 * edit builds it (`islandThroughEngine.js`), and its run is carried to the
 * apply (`_carriedRuns`). Until then the first edit rebuilt 518 of the
 * fixture pool's 522 island layouts — a plain island's one box per row became
 * module widths, a sink island's back row was re-split — and 79 left the
 * dishwasher's end panel in no run until then. Now 0 of 522 (and of 555
 * under Venus) change; the top five options of every kitchen are unchanged.
 * Styling matches the ends of an engine island's slab too (`freestanding`).
 *
 * 1.113.0: kitchen pieces in their runs. A peninsula arm's back panel and the
 * counter strip behind its range are the run engine's (`peninsulaBack.js`) and
 * the arm's run owns them (the strip gains `category.backBridge`); each shallow
 * storage stretch is a 12" run of its own (`shallowRuns.js`), built by the
 * engine — a stretch sharing its wall with the deep run gains the countertop
 * it never had (47B8DEE5, 4 layouts). The apply puts a fridge surround in its
 * run and keeps a strip filler's slab with it. Of the fixture pool's top-5
 * (115 layouts), 109 are unchanged.
 *
 * 1.114.0: option #1 (the closest design) is the kitchen its first edit
 * builds. Every run is carried as a design run in the line a new run gets for
 * this user, on the room's walls, its appliance bays anchored where the card
 * stands them, and solved on the room as the app reads it back — joined where
 * runs turn a corner, each recorded L in a size the line sells. The first edit
 * of every corpus kitchen's option #1 rebuilds nothing and moves no appliance,
 * and its card keeps the 30" clear of a doorway on the next wall.
 *
 * 1.115.0: a drawn kitchen area whose corner sits a hair off a neighbouring
 * section's corner no longer throws in `walkwayScope` (the clip retries on a
 * snap grid), which had failed every typology — 2428 Lore Rd / Scan 1 now
 * generates. A kitchen the exact clip already handled is unchanged.
 *
 * 1.116.0: a wide door-less opening keeps a 48" way through rather than a flat
 * 30" band (#2822). The envelope, the materializer and the run fill share one
 * keep-out (`openingKeepOuts`), decided per candidate with its appliances
 * standing, and the validator counts every piece against a passage. Oxford /
 * Barry Ave's pool goes 106 -> 60 (its U-shapes pinched the archway to ~30");
 * 19 Rapley Close's #3 gains 14" of base run; Golden West's applied run fills
 * to the archway corner.
 *
 * 1.117.0: a blind or empty corner's 3" reveal takes in the leftover beside it
 * (`cornerReveal.js`), so a leg never stands two fillers side by side — gone
 * from all 444 generated layouts that had them — and the uppers' corner strip
 * joins the filler under it rather than standing bare (129 -> 60). A leftover
 * under the narrowest filler goes where a slot shows least.
 *
 * 1.118.0: a sink base against a corner reveal 3" or more past its minimum
 * steps up one size in its line's list, into the reveal
 * (`cornerReveal.widenSinkBasesIntoReveals`). Only Venus sells one over 36"
 * (SB39), so generic-line layouts are unchanged; re-solved as Venus, the one
 * 9-12" reveal beside a sink base becomes 6.25" beside a 39".
 *
 * 1.119.0: what the user chose not to copy from the current cabinets rides on
 * every layout (`_notCopied`, `copiedFromRoom.js`), and a kitchen spanning two
 * rooms carries the kitchen room's cabinet and countertop records into its
 * workspace, so its island and shallow runs wear the room's look as a
 * single-room kitchen's do. Nothing changes for a single-room kitchen that
 * copies everything.
 *
 * 1.120.0: a dishwasher is reserved its standard opening (18" or 24") when its
 * model's body is up to 1" under it, and a wall cabinet fitted down to a size
 * its line sells leaves a filler for the rest, not bare wall (a Venus 21"
 * upper beside 2 5/8" of paint over a 600 mm dishwasher).
 *
 * 1.121.0: a door-less opening over 48" keeps only the stretch of its landing
 * band its walkways use, at least 48", per walkway variant
 * (`openingBandTrim.js`), instead of its whole width. 2428 Lore Rd's kitchen
 * gains its west + south L (0 -> 7 layouts); Golden West's blessed galley
 * closes its run at the archway (re-captured).
 *
 * 1.122.0: a span ending against a corner appliance's clear floor (its depth
 * + 30" on the wall it backs onto) is an offcut like any other and is not
 * built — a lone 9" base on 2428 Lore Rd's south wall. Across the 702 option
 * cards of the stored kitchens, 6 lose exactly one such piece (9-18" base +
 * filler) and nothing else changes; no layout is removed.
 *
 * 1.123.0: the walkway snaps flush to a wall skewed by a hair; a fridge may
 * stand 36" from an inside corner (scored a little lower inside 48"); a
 * peninsula end may leave 36" to the next wall (graded below 42"); a
 * peninsula is refused only when it stands out of the middle of a run. 2425
 * San Fernando Ct gains its L + peninsula. A seated slab no longer reaches a
 * leg's thickness past the other arm's back at a floating L's outer corner,
 * so the counters along that back line up (Jerry's House, BE8E1430).
 *
 * 1.124.0: a run may carry on past a free end — a wing wall's end or a wide
 * opening's jamb — straight on, turned 45° once or twice, or 90°
 * (`continuations/`), keeping 48" of the passage from every piece; walkways
 * route round the counters a layout adds, and what a layout does to the walks
 * (a longer walk, a covered crossing, a way in cut off) and the floor its arms
 * trap are charged or refused; free-standing arms stop at 120"; a run's sink
 * base steps down where 36" does not fit; a dishwasher may stand across a 45°
 * bend; a corner yields to a sink centred on its window; wall runs may grow
 * into a walkway the walk can go round; counter counts only on built walls.
 */
const GENERATOR_VERSION = '1.124.0';

/**
 * Shape of a single entry in `room.cabinetLayout.cabinetWalls`.
 *
 * @typedef {Object} CabinetWall
 * @property {string} id - For `kind: 'real'`, the room.walls[].id. For
 *   imaginary kinds, a fresh uuid unique within `cabinetWalls`.
 * @property {'real'|'peninsula'|'island'} kind
 * @property {{bottomLeft: {x:number,z:number}, bottomRight: {x:number,z:number}}} [alignment]
 *   Inline geometry for imaginary kinds; omitted for `real` (look up
 *   in room.walls instead).
 * @property {{x:number,z:number}} [workingNormal] - Unit vector
 *   perpendicular to alignment, pointing toward the primary working
 *   side. Required for imaginary kinds; for `real`, derived from
 *   room polygon at resolve time.
 * @property {number} [cabinetDepth] - Cabinet depth in meters (default
 *   24" / 0.6096 m). Required for imaginary kinds.
 * @property {boolean} [doubleSided] - True if cabinets exist on BOTH
 *   perpendicular sides of the alignment line (islands, wider
 *   peninsulas with back storage). Default false.
 * @property {CabinetWallAnchor} [anchor] - Required for `peninsula`;
 *   absent for `real` and `island`.
 * @property {boolean} [carriesRun] - `false` on a real wall the layout put
 *   no cabinet run on but that an object still references — a detached
 *   fridge parked against it, an upper corner unit's far leg. The run
 *   deriver emits a run for such a wall only around its appliances and
 *   never joins it to a neighbour. Absent means true.
 * @property {Array<{start:number,end:number}>} [clearances] - Floor along
 *   this wall the layout keeps clear of base cabinets, in the wall's own
 *   params: walkway and island-aisle rectangles, wall solids, an opening's
 *   projected zone, a seating setback, an appliance's corner clearance.
 *   Openings themselves are not listed (the run engine reads them from the
 *   room). The run deriver turns each into a hard `clearance` bay.
 * @property {{start:number,end:number,depth:number}} [seating] - On a
 *   peninsula people sit at: the seated span in the arm's own params and the
 *   knee overhang (metres). The arm's top is one slab with that overhang in
 *   it, and the run engine rebuilds it from this on every re-solve
 *   (`seatedPeninsulaTop.seatRunArms`). Absent on an unseated arm.
 */

/**
 * @typedef {Object} CabinetWallAnchor
 * @property {CabinetWallRef} ref - The surface this peninsula attaches to.
 *   Real walls and other peninsulas are both valid anchor targets,
 *   allowing L/U/Z-shaped peninsula chains.
 * @property {'left'|'right'} end - BL or BR of the anchor's alignment.
 */

/**
 * Reference from an object to a cabinetWalls entry. The `kind` is
 * inlined (redundant with the cabinetWalls entry) so consumers can
 * dispatch real vs imaginary without a separate lookup.
 *
 * @typedef {Object} CabinetWallRef
 * @property {string} id
 * @property {'real'|'peninsula'|'island'} kind
 */

/**
 * Shape of `room.cabinetLayout`.
 *
 * @typedef {Object} CabinetLayout
 * @property {string} typologyKey - e.g. 'L+island', 'single-wall+peninsula'.
 * @property {string} variantLabel - human-readable variant id (e.g.
 *   '4FC19A@right/60x108"').
 * @property {CabinetWall[]} cabinetWalls
 * @property {Array<{start:number,end:number}>} [built] - (on each cabinet wall)
 *   the spans the generator's plan stands something in, merged and in the
 *   wall's own params: the fillable sub-spans, appliances, corner bodies, and
 *   the covers (gap panels, end pieces, an arm's back panel). The run deriver
 *   takes a run's EXTENT from these.
 * @property {Array<{start:number,end:number}>} [bodies] - (on each cabinet
 *   wall) the subset of `built` that is a lower BODY — cabinet spans,
 *   appliances, corner units. The built part of a clearance is read from
 *   these, so a cover standing in a clearance leaves it a clearance. A layout
 *   without either is read off its objects.
 * @property {Array<{a:string,b:string,treatment:Object}>} [corners] - What the
 *   generator DECIDED at each corner between two cabinet walls, in the run
 *   engine's treatment vocabulary (`cabinet-runs/cornerTreatment.js`): `cabinet`,
 *   `blind` (with `extendWallId`, the wall whose run reaches the vertex,
 *   `singleSided` and its `length`), `empty`, `oblique` (with `kite`). The
 *   run deriver records these on the joined run ahead of what the objects
 *   say; absent, the treatment is read off the objects.
 * @property {CabinetLayoutIsland} [island]
 * @property {CabinetLayoutAppliance[]} appliances
 * @property {string} generatorVersion
 * @property {string} generatedAt - ISO timestamp.
 * @property {boolean} stale - True if an architectural change
 *   invalidated this layout and it needs regeneration.
 */

/**
 * @typedef {Object} CabinetLayoutIsland
 * @property {string} id
 * @property {{x:number,z:number}} center
 * @property {{x:number,z:number}} dimensions
 * @property {{y:number}} rotation
 * @property {CabinetWallRef} cabinetWallRef - The island's alignment-line
 *   entry in cabinetWalls (kind: 'island').
 * @property {Object} [sinkSlot] - Present if a sink + DW are placed on
 *   this island (see proposeIsland output).
 */

/**
 * @typedef {Object} CabinetLayoutAppliance
 * @property {string} id - matches a room.objects[].id
 * @property {string} type - 'fridge' | 'sink' | 'range' | 'dishwasher' | etc.
 * @property {CabinetWallRef} cabinetWallRef - which cabinet wall hosts it
 * @property {number} wallParam - position along the wall in meters
 */

/**
 * @typedef {Object} LayoutProvenance
 * @property {string} layoutVersion - matches CabinetLayout.generatorVersion at write time.
 * @property {'auto'|'userEdited'} sourceKey
 */

module.exports = {
  CABINET_WALL_KINDS,
  ANCHOR_ENDS,
  PROVENANCE_SOURCE_KEYS,
  GENERATOR_VERSION,
};
